import React from "react";
import { Link } from "react-router-dom";

const baseStyles =
    "tw:group tw:relative tw:inline-flex tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-full tw:font-medium tw:leading-normal tw:no-underline tw:cursor-pointer tw:h-[44px] tw:px-6 tw:text-[clamp(14px,2vw,20px)] tw:md:h-[50px] tw:md:px-7 tw:xl:h-16 tw:xl:px-10";

const variantStyles = {
    primary: "tw:bg-[#222] tw:!text-white tw:border-0 tw:hover:!text-white tw:focus-visible:!text-white",
    border: "tw:bg-white tw:!text-[#222] tw:border tw:border-black/20 tw:hover:!text-[#222] tw:focus-visible:!text-[#222]",
};

const LETTER_STAGGER_MS = 18;
const LETTER_DURATION_MS = 500;
const LETTER_EASE = "cubic-bezier(0.65, 0, 0.35, 1)";

const maskStyle = {
    display: "inline-block",
    height: "1.35em",
    overflow: "hidden",
    verticalAlign: "middle",
    lineHeight: 1.35,
};

const lineStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "1.35em",
    lineHeight: 1.35,
};

const RevealingLetter = ({ char, index, revealColor }) => {
    const delay = `${index * LETTER_STAGGER_MS}ms`;

    if (char === " ") {
        return <span style={{ display: "inline-block", width: "0.28em" }} aria-hidden="true" />;
    }

    return (
        <span style={maskStyle}>
            <span
                className="tw:block tw:will-change-transform tw:transition-transform tw:group-hover:-translate-y-[1.35em] tw:group-focus-visible:-translate-y-[1.35em]"
                style={{
                    transitionDuration: `${LETTER_DURATION_MS}ms`,
                    transitionTimingFunction: LETTER_EASE,
                    transitionDelay: delay,
                }}
            >
                <span style={lineStyle}>{char}</span>
                <span style={{ ...lineStyle, ...(revealColor ? { color: revealColor } : {}) }} aria-hidden="true">
                    {char}
                </span>
            </span>
        </span>
    );
};

const ButtonInner = ({ children, revealColor }) => {
    const label = typeof children === "string" ? children : null;
    const chars = label ? Array.from(label) : null;

    if (!chars?.length) {
        return (
            <span style={maskStyle}>
                <span
                    className="tw:block tw:will-change-transform tw:transition-transform tw:group-hover:-translate-y-[1.35em] tw:group-focus-visible:-translate-y-[1.35em]"
                    style={{
                        transitionDuration: `${LETTER_DURATION_MS}ms`,
                        transitionTimingFunction: LETTER_EASE,
                    }}
                >
                    <span style={lineStyle}>{children}</span>
                    <span
                        style={{ ...lineStyle, ...(revealColor ? { color: revealColor } : {}) }}
                        aria-hidden="true"
                    >
                        {children}
                    </span>
                </span>
            </span>
        );
    }

    return (
        <span className="tw:inline-flex tw:items-center tw:whitespace-nowrap" aria-hidden="true">
            {chars.map((char, index) => (
                <RevealingLetter
                    key={`${char}-${index}`}
                    char={char}
                    index={index}
                    revealColor={revealColor}
                />
            ))}
        </span>
    );
};

/** Letter-stagger text reveal — reuse on links with `tw:group` */
export const TextReveal = ButtonInner;

export default function NewButton({
    children,
    to,
    href,
    variant = "primary",
    arrow = false,
    className = "",
    style,
    tone,
    revealColor,
    ...props
}) {
    const useTone = Boolean(tone?.bg || tone?.color);
    const variantClass = useTone
        ? "tw:border-0"
        : (variantStyles[variant] || "");
    const finalClassName = `${baseStyles} ${variantClass} ${className}`
        .trim()
        .replace(/\s+/g, " ");
    const finalStyle = useTone
        ? {
            ...style,
            ...(tone.bg ? { backgroundColor: tone.bg } : {}),
            ...(tone.color ? { color: tone.color } : {}),
        }
        : style;

    const ariaLabel = typeof children === "string" ? children : undefined;
    const content = arrow ? (
        <span className="tw:inline-flex tw:items-center tw:gap-3">
            <ButtonInner revealColor={revealColor}>{children}</ButtonInner>
            <span
                aria-hidden="true"
                className="tw:inline-block tw:transition-transform tw:duration-500 tw:ease-[cubic-bezier(0.65,0,0.35,1)] tw:group-hover:translate-x-1.5 tw:group-focus-visible:translate-x-1.5"
            >
                →
            </span>
        </span>
    ) : (
        <ButtonInner revealColor={revealColor}>{children}</ButtonInner>
    );

    if (to) {
        return (
            <Link to={to} className={finalClassName} style={finalStyle} aria-label={ariaLabel} {...props}>
                {content}
            </Link>
        );
    }

    if (href) {
        return (
            <a href={href} className={finalClassName} style={finalStyle} aria-label={ariaLabel} {...props}>
                {content}
            </a>
        );
    }

    return (
        <button type="button" className={finalClassName} style={finalStyle} aria-label={ariaLabel} {...props}>
            {content}
        </button>
    );
}